// src/platform/firebase/runtime.ts

export type FirebaseRuntimeTarget = 'client' | 'admin'
export type FirebaseStage = 'local' | 'dev' | 'staging' | 'prod'

export type ClientOptions = {
  siteKey?: string // App Check
  useEmulators: boolean
  functionsRegion?: string
}

export type AdminOptions = {
  // When running against emulators with Admin SDK,
  // we rely on standard env vars (see adminFactory.ts)
  projectId: string
  useEmulators: boolean
}

export type FirebaseRuntimeConfig = {
  stage: FirebaseStage
  target: FirebaseRuntimeTarget
  client: ClientOptions
  admin: AdminOptions
  // raw config used by initializeApp (client SDK)
  webConfig?: {
    apiKey: string
    authDomain: string
    projectId: string
    storageBucket: string
    messagingSenderId: string
    appId: string
  }
}

/**
 * Resolve runtime for both client and admin contexts.
 * - On the web, read Vite envs.
 * - In Node, prefer process.env (scripts, tests, migrations).
 */
export function resolveFirebaseRuntime(target: FirebaseRuntimeTarget): FirebaseRuntimeConfig {
  const isBrowser = typeof window !== 'undefined'
  const mode =
    (isBrowser ? import.meta.env.MODE : process.env.NODE_ENV) ??
    (isBrowser ? 'development' : 'development')

  // Stage derivation — adapt to your naming
  const explicitStage = (isBrowser ? import.meta.env.VITE_STAGE : process.env.STAGE) as
    | FirebaseStage
    | undefined

  // Derive stage from mode if not explicitly set
  // NODE_ENV=local -> stage=local (with emulators)
  // NODE_ENV=dev -> stage=dev (live dev project, no emulators)
  // NODE_ENV=prod/production -> stage=prod (live prod, no emulators)
  // mode=test -> stage=local (with emulators for tests)
  const stage: FirebaseStage =
    explicitStage ??
    (mode === 'local'
      ? 'local'
      : mode === 'test'
        ? 'local'
        : mode === 'production' || mode === 'prod'
          ? 'prod'
          : mode === 'dev'
            ? 'dev'
            : 'local')

  // Emulators? Only use emulators when:
  // - stage is explicitly 'local'
  // - OR in browser and on localhost
  // - OR in Node.js and FIRESTORE_EMULATOR_HOST is set
  const useEmulators =
    stage === 'local' ||
    (isBrowser
      ? window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      : !!process.env.FIRESTORE_EMULATOR_HOST) // any emulator var means local

  // Read Firebase config from environment variables
  // Browser: use import.meta.env (Vite)
  // Node: use process.env
  const projectId =
    (isBrowser
      ? (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined)
      : (process.env.FIREBASE_PROJECT_ID as string | undefined)) || ''

  // Web config (only for client, only in browser)
  const webConfig = isBrowser
    ? {
        apiKey: (import.meta.env.VITE_FIREBASE_API_KEY as string) || '',
        authDomain: (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string) || '',
        projectId,
        storageBucket: (import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string) || '',
        messagingSenderId: (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string) || '',
        appId: (import.meta.env.VITE_FIREBASE_APP_ID as string) || '',
      }
    : undefined

  const siteKey = isBrowser
    ? (import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY as string | undefined)
    : undefined
  const functionsRegion =
    (isBrowser ? import.meta.env.VITE_FUNCTIONS_REGION : process.env.FUNCTIONS_REGION) ||
    'us-central1'

  return {
    stage,
    target,
    client: { siteKey, useEmulators, functionsRegion },
    admin: { projectId, useEmulators },
    webConfig,
  }
}
